<template>
  <Popup 
    :show="modelValue" 
    position="bottom" 
    round 
    :overlay="false"
    @update:show="handlePopupUpdate"
  >
    <div ref="keyboardContainer" :class="keyboardClass">
      <!-- 大写状态指示器 -->
      <div v-if="isShiftActive" class="caps-lock-indicator">
        <div class="indicator-line"></div>
        <span class="indicator-text">大写已开启</span>
      </div>
    </div>
  </Popup>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { Popup } from 'vant'
import Keyboard from 'simple-keyboard'
import 'simple-keyboard/build/css/index.css'

// 定义 Props
const props = defineProps({
  keyboardClass: {
    type: String,
    default: 'simple-keyboard'
  },
  input: {
    type: String,
    default: ''
  },
  maxLength: {
    type: [String, Number],
    default: ''
  },
  layoutMode: {
    type: String,
    default: 'default'
  },
  modelValue: {
    type: Boolean,
    default: false
  }
})

// 定义 Emits
const emit = defineEmits([
  'change', 
  'keyPress', 
  'layoutChange',
  'update:modelValue'
])

// 响应式数据
const keyboard = ref(null)
const currentLayoutMode = ref(props.layoutMode)
const keyboardContainer = ref(null)
const isShiftActive = ref(false) // 大写状态标志

// 处理Popup的show状态变化
const handlePopupUpdate = (show) => {
  emit('update:modelValue', show)
}

// 键盘显示文本
const displayDefault = ref({
  '{bksp}': '⌫',
  '{shift}': '⇧',
  '{enter}': '↲',
  '{space}': '空格',
  '{clear}': '清除',
  '{symbol}': '!?#',
  '{number}': '123',
  '{comma}': ',',
  '{period}': '.',
  '{return}': '返回',
  '{hide}': '收起'
})

// 键盘布局
const layout = {
  'default': [
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m {bksp}',
    '{symbol} {number} {space} {comma} {period} {hide}'
  ],
  'shift': [
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M {bksp}',
    '{symbol} {number} {space} {comma} {period} {hide}'
  ],
  'symbol': [
    '! @ # $ % ^ & * ( )',
    '- _ + = [ ] { } | \\',
    ': ; " \' ` ~ < > ?',
    '{return} {number} {space} {comma} {period} {hide}'
  ],
  'number': [
    '1 2 3',
    '4 5 6', 
    '7 8 9',
    '{return} 0 {hide}'
  ]
}

// 初始化键盘
const initKeyboard = () => {
  if (!keyboardContainer.value) {
    console.error('Keyboard container not found')
    return false
  }

  // 清理已存在的键盘实例
  if (keyboard.value) {
    try {
      keyboard.value.destroy()
    } catch (error) {
      console.warn('Error destroying old keyboard:', error)
    }
    keyboard.value = null
  }

  try {
    keyboard.value = new Keyboard(keyboardContainer.value, {
      onChange: onChange,
      onKeyPress: onKeyPress,
      layout: layout,
      display: displayDefault.value,
      layoutName: props.layoutMode,
      maxLength: props.maxLength,
      preventMouseDownDefault: true,
      buttonTheme: [
        {
          class: "hg-function-key",
          buttons: "{bksp} {shift} {enter} {symbol} {number} {comma} {period} {return} {hide}"
        },
        {
          class: "hg-delete-key", // 专门为删除按钮设置的类
          buttons: "{bksp}"
        },
        {
          class: "hg-space-key",
          buttons: "{space}"
        },
        {
          class: "hg-return-key",
          buttons: "{return}"
        },
        {
          class: "hg-hide-key",
          buttons: "{hide}"
        }
      ]
    })
    
    console.log('Keyboard initialized successfully')
    return true
  } catch (error) {
    console.error('Keyboard initialization error:', error)
    return false
  }
}

// 方法
const onChange = (input) => {
  emit('change', input)
}

const onKeyPress = (button) => {
  console.log('Key pressed:', button)
  switch(button) {
    case '{shift}':
      handleShift()
      break
    case '{symbol}':
      switchLayout('symbol')
      break
    case '{number}':
      switchLayout('number')
      break
    case '{return}':
      switchLayout('default')
      break
    case '{clear}':
      handleClear()
      break
    case '{hide}':
      emit('update:modelValue', false)
      break
  }
  emit('keyPress', button)
}

const handleShift = () => {
  if (!keyboard.value) {
    console.error('Keyboard not initialized')
    return
  }
  
  try {
    const currentLayout = keyboard.value.options.layoutName
    const newLayout = currentLayout === 'default' ? 'shift' : 'default'
    console.log('Switching layout from', currentLayout, 'to', newLayout)
    
    // 更新大写状态
    isShiftActive.value = (newLayout === 'shift')
    
    keyboard.value.setOptions({ layoutName: newLayout })
    currentLayoutMode.value = newLayout
    
    // 更新Shift按钮的激活状态
    updateShiftButtonState(newLayout)
  } catch (error) {
    console.error('Error handling shift:', error)
  }
}

// 更新Shift按钮状态
const updateShiftButtonState = (layoutName) => {
  if (!keyboard.value) return
  
  try {
    // 获取Shift按钮元素并添加/移除激活类
    const shiftButton = keyboardContainer.value.querySelector('.hg-button[data-skbtn="{shift}"]')
    if (shiftButton) {
      if (layoutName === 'shift') {
        shiftButton.classList.add('shift-active')
      } else {
        shiftButton.classList.remove('shift-active')
      }
    }
  } catch (error) {
    console.error('Error updating shift button state:', error)
  }
}

const switchLayout = (layoutName) => {
  if (!keyboard.value) {
    console.error('Keyboard not initialized')
    return
  }
  
  try {
    console.log('Switching to layout:', layoutName)
    currentLayoutMode.value = layoutName
    
    // 切换到非字母布局时关闭大写状态
    if (layoutName !== 'default' && layoutName !== 'shift') {
      isShiftActive.value = false
    }
    
    keyboard.value.setOptions({
      layoutName: layoutName
    })
    emit('layoutChange', layoutName)
  } catch (error) {
    console.error('Error switching layout:', error)
  }
}

const handleClear = () => {
  if (!keyboard.value) {
    console.error('Keyboard not initialized')
    return
  }
  
  try {
    keyboard.value.clearInput()
    emit('change', '')
  } catch (error) {
    console.error('Error clearing input:', error)
  }
}

// 监听键盘显示状态
watch(() => props.modelValue, async (newVal) => {
  console.log('Keyboard visibility changed to:', newVal)
  
  if (newVal) {
    await nextTick()
    setTimeout(() => {
      const success = initKeyboard()
      if (!success) {
        console.error('Failed to initialize keyboard')
        emit('update:modelValue', false)
      }
    }, 100)
  } else {
    if (keyboard.value) {
      try {
        keyboard.value.destroy()
        keyboard.value = null
        console.log('Keyboard destroyed')
      } catch (error) {
        console.warn('Error destroying keyboard:', error)
      }
    }
    // 隐藏键盘时重置大写状态
    isShiftActive.value = false
  }
})

// 生命周期
onMounted(() => {
  console.log('Keyboard component mounted')
  if (props.modelValue) {
    nextTick(() => {
      initKeyboard()
    })
  }
})
onBeforeUnmount(() => {
  console.log('Keyboard component unmounting')
  if (keyboard.value) {
    try {
      keyboard.value.destroy()
    } catch (error) {
      console.warn('Error destroying keyboard on unmount:', error)
    }
  }
})

// 其他监听器
watch(() => props.input, (value) => {
  if (keyboard.value && value !== keyboard.value.getInput()) {
    try {
      keyboard.value.setInput(value)
    } catch (error) {
      console.error('Error setting input:', error)
    }
  }
})

watch(() => props.layoutMode, (newMode) => {
  if (newMode !== currentLayoutMode.value) {
    switchLayout(newMode)
  }
})
</script>

<style scoped>
.simple-keyboard {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 10px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-height: 300px;
  position: relative;
}

/* 大写状态指示器 */
.caps-lock-indicator {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(25, 113, 194, 0.1);
  padding: 4px 12px;
  border-radius: 16px;
  z-index: 10;
}

.indicator-line {
  width: 20px;
  height: 2px;
  background: #1971c2;
  position: relative;
}

.indicator-line::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, transparent, #1971c2, transparent);
  opacity: 0.3;
}

.indicator-text {
  font-size: 12px;
  color: #1971c2;
  font-weight: 500;
}
</style>

<style>
.simple-keyboard .simple-keyboard {
  background: transparent !important;
  padding: 0 !important;
}

.simple-keyboard .hg-row {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.simple-keyboard .hg-button {
  min-width: 44px;
  height: 52px;
  margin: 0 3px;
  border: none;
  border-radius: 8px;
  background: #ffffff;
  color: #333333;
  font-size: 36px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.simple-keyboard .hg-button:hover {
  background: #f8f8f8;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

.simple-keyboard .hg-button:active {
  background: #e8e8e8;
  transform: translateY(0px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Shift按钮激活状态样式 */
.simple-keyboard .hg-button[data-skbtn="{shift}"].shift-active {
  background: #1971c2 !important;
  color: white !important;
  position: relative;
}

.hg-button{
  font-size: 45px;
  
}
.simple-keyboard .hg-button[data-skbtn="{shift}"].shift-active::after {
  content: '';
  position: absolute;
  bottom: 23%;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 2px;
  background: white;
  border-radius: 1px;
}

.simple-keyboard .hg-function-key {
  background: #e9ecef !important;
  color: #495057 !important;
  font-size: 36px;
  font-weight: 600 !important;
}

.hg-return-key{
  white-space: nowrap;
  font-size: 30px !important;
}
/* 删除按钮新背景色 - 改为橙色系 */
.simple-keyboard .hg-delete-key {
  background: linear-gradient(135deg, #35bfff57 0%, #53f9ff57 100%) !important;
  color: white !important;
  font-size: 35px !important;
  font-weight: 600 !important;
}
.simple-keyboard .hg-space-key {
  flex: 4 !important;
  min-width: 280px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
  border: 1px solid #dee2e6 !important;
}
.simple-keyboard .hg-shift-large {
  font-size: 24px !important; 
}
.simple-keyboard .hg-hide-key {
  background: #e9ecef !important;
  color: #495057 !important;
  font-size: 30px !important;
  white-space: nowrap;
}
/* 响应式设计
@media (max-width: 768px) {
  .simple-keyboard .hg-button {
    min-width: 38px;
    height: 46px;
    font-size: 30px;
  }
  
  .simple-keyboard .hg-space-key {
    min-width: 220px !important;
  }
  
  .caps-lock-indicator {
    top: 4px;
  }
  
  .indicator-text {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .simple-keyboard .hg-button {
    min-width: 32px;
    height: 40px;
    font-size: 14px;
  }
  
  .simple-keyboard .hg-space-key {
    min-width: 180px !important;
  }
  
  .caps-lock-indicator {
    top: 2px;
    padding: 2px 8px;
  }
  
  .indicator-text {
    font-size: 10px;
  }
} */
</style>